import React, { useEffect, useState } from 'react';
import { Button, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { PaymentMethodResult } from '@stripe/stripe-js';
import Rodal from '../../../../components/Rodal/Rodal';
import CardPaymentForm from '../../../../components/CardPaymentForm/CardPaymentForm';
import ButtonLoader from '../../../../components/Layout/Buttons/ButtonLoader';
import createNotification from '../../../../utils/createNotification';
import { useStateSelector } from '../../../../store/selectors';
import PoweredByStripe from '../../../../assets/images/poweredByStripe.png';
import InfoTooltip from '../../../../components/info-tooltip/info-tooltip';
import './SaveMeetingCardModal.scss';

export interface MeetingPaymentMethodData {
    shouldUseSavedPaymentMethod: boolean;
    stripePaymentMethodId: string;
}

interface SaveMeetingCardModalProps {
    isVisible: boolean;
    onClose: () => void;
    onAuthorize: (data: MeetingPaymentMethodData) => void;
    meetingFee?: number;
    isBuyerTm?: boolean;
}

const SaveMeetingCardModal = (props: SaveMeetingCardModalProps) => {
    const stripe = useStripe();
    const elements = useElements();
    const axios = useStateSelector((state) => state.core.axios);

    const [isCardLoaded, setIsCardLoaded] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCardDataValid, setIsCardDataValid] = useState(false);
    const [savedCard, setSavedCard] = useState(null);
    const [isCardExist, setIsCardExist] = useState(false);
    const [isUseDifferentMethodChecked, setIsUseDifferentMethodChecked] =
        useState(true);
    const [isCardSelected, setIsCardSelected] = useState(true);
    const [shouldSavePaymentMethod, setShouldSavePaymentMethod] =
        useState(true);

    useEffect(() => {
        if (props.isVisible) {
            if (props.isBuyerTm) {
                setIsCardLoaded(true);
            } else {
                fetchSavedCard();
            }
        }
    }, [props.isVisible]);

    const fetchSavedCard = () => {
        setIsCardLoaded(false);
        axios
            .get('api/Wallet/PaymentMethod')
            .then((response: any) => {
                if (response.data?.lastDigits) {
                    setSavedCard(response.data);
                    setIsCardExist(true);
                    setIsUseDifferentMethodChecked(false);
                    setIsCardSelected(true);
                }
            })
            .finally(() => setIsCardLoaded(true));
    };

    const handleSubmit = () => {
        if (!props.isBuyerTm && !isUseDifferentMethodChecked) {
            props.onAuthorize({ shouldUseSavedPaymentMethod: true, stripePaymentMethodId: '' });
            return;
        }

        setIsSubmitting(true);
        stripe
            .createPaymentMethod({
                type: 'card',
                card: elements.getElement('cardNumber'),
            })
            .then((result: PaymentMethodResult) => {
                if (result?.paymentMethod) {
                    if (!props.isBuyerTm && shouldSavePaymentMethod) {
                        axios
                            .post('api/Wallet/PaymentMethod', {
                                paymentMethodId: result.paymentMethod.id,
                                shouldUpdatePaymentMethod: isCardExist,
                            })
                            .then((response: any) => {
                                if (response?.status === 200) {
                                    createNotification(
                                        'Payment method saved successfully.',
                                        'success'
                                    );
                                    props.onAuthorize({ shouldUseSavedPaymentMethod: true, stripePaymentMethodId: '' });
                                } else {
                                    createNotification(
                                        'Could not save payment method.',
                                        'error'
                                    );
                                }
                            })
                            .finally(() => setIsSubmitting(false));
                    } else {
                        props.onAuthorize({ shouldUseSavedPaymentMethod: false, stripePaymentMethodId: result.paymentMethod.id });
                        setIsSubmitting(false);
                    }
                } else if (result.error?.message) {
                    createNotification(result.error.message, 'error');
                    setIsSubmitting(false);
                }
            })
            .catch(() => {
                createNotification(
                    'Something went wrong during card validation.',
                    'error'
                );
                setIsSubmitting(false);
            });
    };

    if (!props.isVisible || !isCardLoaded) {
        return null;
    }

    return (
        <div className="save-meeting-card-modal">
            <Rodal
                visible={props.isVisible}
                onClose={props.onClose}
                animation={'slideDown'}
                width={520}
                center>
                <ModalHeader className="popup-header">
                    <span className="meeting-fee-header">
                        Authorize Meeting Fee:
                        <InfoTooltip
                            idText="authorize-meeting-fee"
                            text="Payment will be charged after the meeting is confirmed."
                        />
                        {props.meetingFee != null && (
                            <span className="meeting-fee-price">${props.meetingFee.toLocaleString()}</span>
                        )}
                    </span>
                </ModalHeader>
                <CardPaymentForm
                    doesCardExist={props.isBuyerTm ? false : isCardExist}
                    setIsUseDifferentMethodChecked={props.isBuyerTm ? () => {} : setIsUseDifferentMethodChecked}
                    setIsCardSelected={props.isBuyerTm ? () => {} : setIsCardSelected}
                    isUseDifferentMethodChecked={props.isBuyerTm ? true : isUseDifferentMethodChecked}
                    isCardSelected={props.isBuyerTm ? true : isCardSelected}
                    savedCard={props.isBuyerTm ? null : savedCard}
                    shouldSavePaymentMethod={props.isBuyerTm ? false : shouldSavePaymentMethod}
                    setShouldSavePaymentMethod={props.isBuyerTm ? () => {} : setShouldSavePaymentMethod}
                    hideSavePaymentMethod={props.isBuyerTm}
                    setIsFormValid={setIsCardDataValid}
                />
                <ModalFooter className="flex-footer">
                    <Row className="buttons-row">
                        <Button
                            className="btn close-button"
                            onClick={props.onClose}>
                            Cancel
                        </Button>
                        <ButtonLoader
                            className="btn btn-primary submit-button"
                            isLoading={isSubmitting}
                            loaderButtonText=""
                            disabled={
                                isSubmitting ||
                                ((props.isBuyerTm || isUseDifferentMethodChecked) && !isCardDataValid)
                            }
                            buttonText="Authorize Meeting Fee"
                            onClick={handleSubmit}
                        />
                    </Row>
                </ModalFooter>
                <div className="default-div-image">
                    <img className="default-image" src={PoweredByStripe} alt="Powered by Stripe" />
                </div>
            </Rodal>
        </div>
    );
};

export default SaveMeetingCardModal;
